import { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';

export function ForgotPassword() {
  const { state } = useAuth();
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // If already authenticated, redirect to dashboard
  if (state.status === 'authenticated') {
    return <Navigate to="/dashboard" replace />;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!email) {
      setError(t('forgotPassword.emailRequired', { defaultValue: 'Please enter your email address.' }));
      return;
    }

    setIsLoading(true);
    try {
      await fetch('/internal/v1/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      // Always show success regardless of response (security: don't reveal if email exists)
      setSubmitted(true);
    } catch {
      // Even on network error, show success for security
      setSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-dvh animated-gradient-bg relative flex items-start sm:items-center justify-center">
      {/* Top-left: Unifiber logo */}
      <div className="absolute top-4 left-4 sm:top-8 sm:left-10 z-10">
        <img src="/UnifiberLogoWhite.png" alt="Unifiber" className="h-8 sm:h-12 opacity-90" />
      </div>

      {/* Glass card */}
      <div className="relative z-10 px-6 pt-20 pb-6 sm:py-12 w-full flex items-center justify-center">
        <div className="glass-card-login rounded-2xl p-6 sm:p-10 w-full max-w-md space-y-6">
          {submitted ? (
            /* ── Success state ────────────────────────────────── */
            <div className="space-y-6">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center">
                  <CheckCircle className="w-7 h-7 text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{t('forgotPassword.successTitle')}</h2>
                  <p className="text-white/60 mt-2 text-sm leading-relaxed">
                    {t('forgotPassword.successMessage', { email })}
                  </p>
                </div>
              </div>

              <Link
                to="/login"
                className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose hover:-translate-y-px hover:shadow-[0_8px_25px_rgba(237,18,95,0.4)] transition-all duration-300 flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('forgotPassword.backToLogin')}
              </Link>
            </div>
          ) : (
            /* ── Request form state ──────────────────────────── */
            <div className="space-y-6">
              {/* Header */}
              <div>
                <h2 className="text-xl font-bold text-white">{t('forgotPassword.title')}</h2>
                <p className="text-white/60 mt-1 text-sm">
                  {t('forgotPassword.subtitle')}
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="px-4 py-2.5 rounded-lg bg-red-500/20 border border-red-400/30 text-red-200 text-sm font-medium">
                    {error}
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-1.5">
                    {t('forgotPassword.emailLabel')}
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('forgotPassword.emailPlaceholder')}
                      disabled={isLoading}
                      className="glass-input w-full px-4 py-2.5 rounded-lg text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 disabled:opacity-50 pl-10"
                    />
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose hover:-translate-y-px hover:shadow-[0_8px_25px_rgba(237,18,95,0.4)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
                >
                  {isLoading && (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  )}
                  {isLoading ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
                </button>
              </form>

              {/* Back to login */}
              <div className="pt-2">
                <Link
                  to="/login"
                  className="flex items-center justify-center gap-1.5 text-sm text-white/60 hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  {t('forgotPassword.backToLogin')}
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom copyright */}
      <div className="absolute bottom-6 left-0 right-0 text-center z-10">
        <p className="text-white/25 text-xs">&copy; 2026 Unifiber Belgium</p>
      </div>
    </div>
  );
}
